import type { Organism } from '@verndale/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reactModule = (component: any, nodelist: NodeListOf<HTMLElement>) => {
  void Promise.all([import('react'), import('./helpers/react-19-shim')]).then(
    ([React, { renderElement }]) => {
      nodelist.forEach((el: HTMLElement) => {
        // Extract data attributes as props using dataset API.
        const props = { ...el.dataset };

        void renderElement(React.createElement(component, props), el);
      });
    }
  );
};

const modules: Organism[] = [
  {
    name: 'highlight',
    loader: () => import('./components/highlight')
  },
  {
    name: 'logout-confirmation-modal',
    loader: () => import('./components/logoutConfirmationModal')
  },
  {
    name: 'search-input',
    loader: () => import('./components/search/search-input')
  },
  {
    name: 'accordion',
    loader: () => import('./modules/accordion')
  },
  {
    name: 'account-info-block',
    loader: () => import('./modules/accountInfoBlock')
  },
  {
    name: 'address-book-row',
    loader: () => import('./modules/addressBookRow')
  },
  {
    name: 'add-to-cart-button',
    loader: () => import('./modules/addToCartButton')
  },
  {
    name: 'alert-banners',
    loader: () => import('./modules/alert-banners')
  },
  {
    name: 'catalog-video-carousel',
    loader: () => import('./modules/catalogVideoCarousel')
  },
  {
    name: 'child-carousel',
    loader: () => import('./modules/childCarousel')
  },
  {
    name: 'child-detail-analytics',
    loader: () => import('./modules/childDetailAnalytics')
  },
  {
    name: 'confirm-your-address',
    loader: () => import('./modules/confirmYourAddress')
  },
  {
    name: 'contact-us-form',
    loader: () => import('./modules/contactUsForm')
  },
  {
    name: 'donation-successful-masthead',
    loader: () => import('./modules/donationSuccessfulMasthead')
  },
  {
    name: 'edit-contact-info-modal',
    loader: () => import('./modules/editContactInfoModal')
  },
  {
    name: 'featured-stories-row',
    loader: () => import('./modules/featuredStoriesRow')
  },
  {
    name: 'fullscreen-carousel',
    loader: () => import('./modules/fullscreenCarousel')
  },
  {
    name: 'gift-added-modal',
    loader: () => import('./modules/giftAddedModal')
  },
  {
    name: 'gift-detail-masthead',
    loader: () => import('./modules/giftDetailMasthead')
  },
  {
    name: 'give-a-gift-modal',
    loader: () => import('./modules/giveAGiftModal')
  },
  {
    name: 'globe-container',
    loader: () => import('./modules/globeContainer')
  },
  {
    name: 'handling-fee-preference-modal',
    loader: () => import('./modules/handlingFeePreferenceModal')
  },
  {
    name: 'header',
    loader: () => import('./modules/header')
  },
  {
    name: 'image-carousel',
    loader: () => import('./modules/imageCarousel')
  },
  {
    name: 'login-event',
    loader: () => import('./modules/loginEvent/loginEvent')
  },
  {
    name: 'logo-carousel',
    loader: () => import('./modules/logoCarousel')
  },
  {
    name: 'make-a-donation',
    loader: () => import('./modules/makeADonation')
  },
  {
    name: 'media-carousel',
    loader: () => import('./modules/mediaCarousel')
  },
  {
    name: 'media-container',
    loader: () => import('./modules/mediaContainer')
  },
  {
    name: 'my-account-menu',
    loader: () => import('./modules/myAccountMenu')
  },
  {
    name: 'preferences-block',
    loader: () => import('./modules/preferencesBlock')
  },
  {
    name: 'primary-navigation-desktop',
    loader: () => import('./modules/primaryNavigationDesktop')
  },
  {
    name: 'primary-navigation-mobile',
    loader: () => import('./modules/primaryNavigationMobile')
  },
  {
    name: 'progress-indicator',
    loader: () => import('./modules/progressIndicator')
  },
  {
    name: 'recommended-gifts-row',
    loader: () => import('./modules/recommendedGiftsRow')
  },
  {
    name: 'recommended-reading-carousel',
    loader: () => import('./modules/recommendedReadingCarousel')
  },
  {
    name: 'related-media-carousel',
    loader: () => import('./modules/relatedMediaCarousel')
  },
  {
    name: 'scrolling-stat-carousel',
    loader: () => import('./modules/scrollingStatCarousel')
  },
  {
    name: 'search-bar',
    loader: () => import('./modules/searchBar')
  },
  {
    name: 'session-expiration',
    loader: () => import('./modules/sessionExpiration')
  },
  {
    name: 'social-share-row',
    loader: () => import('./modules/socialShareRow')
  },
  {
    name: 'sponsor-a-child',
    loader: () => import('./modules/sponsorAChild')
  },
  {
    name: 'team-card',
    loader: () => import('./components/teamCard')
  },
  {
    name: 'three-card-row',
    loader: () => import('./modules/threeCardRow')
  },
  {
    name: 'turnstile',
    loader: () => import('./modules/turnstile')
  },
  {
    name: 'upgrade-gift-modal',
    loader: () => import('./modules/upgradeGiftModal')
  },
  {
    name: 'add-payment-method-modal',
    loader: () =>
      import('./react-modules/e-commerce/add-payment-method-modal/addPaymentMethodModal'),
    render: reactModule
  },
  {
    name: 'checkout-page',
    loader: () => import('./react-modules/e-commerce/pages/checkoutPage'),
    render: reactModule
  },
  {
    name: 'children-listing',
    loader: () => import('./react-modules/listings/childrenListing'),
    render: reactModule
  },
  {
    name: 'donate-form',
    loader: () => import('./react-modules/donation/donateForm'),
    render: reactModule
  },
  {
    name: 'entra-id-payment',
    loader: () => import('./react-modules/entra-id-payment/entraIdPaymentWizard'),
    render: reactModule
  },
  {
    name: 'gift-listing',
    loader: () => import('./react-modules/listings/giftListing'),
    render: reactModule
  },
  {
    name: 'global-donate-modal',
    loader: () => import('./react-modules/donation/globalDonateModal'),
    render: reactModule
  },
  {
    name: 'help-article-listing',
    loader: () => import('./react-modules/listings/helpArticleListing'),
    render: reactModule
  },
  {
    name: 'impersonate',
    loader: () => import('./react-modules/impersonate/impersonate'),
    render: reactModule
  },
  {
    name: 'impersonate-banner',
    loader: () => import('./react-modules/impersonate/impersonateBanner'),
    render: reactModule
  },
  {
    name: 'media-listing',
    loader: () => import('./react-modules/listings/mediaListing'),
    render: reactModule
  },
  {
    name: 'my-basket-page',
    loader: () => import('./react-modules/my-basket/myBasketPage'),
    render: reactModule
  },
  {
    name: 'my-donations-table',
    loader: () => import('./react-modules/transaction-tables/myDonationsTable'),
    render: reactModule
  },
  {
    name: 'my-letters-container',
    loader: () => import('./react-modules/my-correspondence/MyLettersContainer'),
    render: reactModule
  },
  {
    name: 'my-sponsorships-container',
    loader: () => import('./react-modules/listings/mySponsorshipsContainer'),
    render: reactModule
  },
  {
    name: 'my-transactions-table',
    loader: () => import('./react-modules/transaction-tables/myTransasctionsTable'),
    render: reactModule
  },
  {
    name: 'order-summary',
    loader: () => import('./react-modules/e-commerce/components/orderSummaryWithItems'),
    render: reactModule
  },
  {
    name: 'payment-methods-table',
    loader: () => import('./react-modules/transaction-tables/paymentMethodsTable'),
    render: reactModule
  },
  {
    name: 'pending-updates-transactions-table',
    loader: () => import('./react-modules/transaction-tables/pendingUpdatesTransactionsTable'),
    render: reactModule
  },
  {
    name: 'search-results-listing',
    loader: () => import('./react-modules/listings/searchResultsListing'),
    render: reactModule
  },
  {
    name: 'village-table',
    loader: () => import('./react-modules/village-tables/VillageTable'),
    render: reactModule
  }
];

export default modules;
